import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from '@/components/ui/multi-select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von12Genetic } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';
import { GeneticConditionAutocomplete } from '@/components/common/GeneticConditionAutocomplete';

interface Von12GeneticFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

// Legacy genetic_condition_type labels, kept only to display old records that
// have no genetic_condition_icd10_code yet (read-only, until re-saved).
const LEGACY_GENETIC_CONDITION_LABELS: Record<string, string> = {
  '1': 'Trisomie 21 (Down)',
  '2': 'Trisomie 18 (Edwards)',
  '3': 'Trisomie 13 (Patau)',
  '4': 'Sindrom Turner',
  '5': 'Sindrom Klinefelter',
  '99': 'Altele'
};

const GENETIC_TEST_TYPE_OPTIONS = [
  { value: '1', label: '1 = Cariotip' },
  { value: '2', label: '2 = FISH' },
  { value: '3', label: '3 = Array CGH' }
];

// genetic_test_type is stored as a comma-separated list of codes (e.g. "1,3");
// anything that is not a known code (old free text) is ignored.
const parseGeneticTestTypes = (raw: string | null | undefined): string[] =>
  (raw || '')
    .split(/[^0-9]+/)
    .filter((code) => GENETIC_TEST_TYPE_OPTIONS.some((option) => option.value === code));

export const Von12GeneticForm: React.FC<Von12GeneticFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [geneticConditionPresent, setGeneticConditionPresent] = useState(false);
  const [geneticTestType, setGeneticTestType] = useState<string[]>([]);
  const [geneticTestDate, setGeneticTestDate] = useState<string>('');
  const [geneticTestResult, setGeneticTestResult] = useState<string>('');
  const [geneticConditionType, setGeneticConditionType] = useState<string>('');
  const [geneticConditionIcd10Code, setGeneticConditionIcd10Code] = useState<string | null>(null);
  const [geneticConditionOther, setGeneticConditionOther] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getGenetic(patientId);

      if (data) {
        setHasExistingData(true);
        setGeneticConditionPresent(Boolean(data.genetic_condition_present));
        setGeneticTestType(parseGeneticTestTypes(data.genetic_test_type));
        setGeneticTestDate(data.genetic_test_date || '');
        setGeneticTestResult(data.genetic_test_result?.toString() || '');
        setGeneticConditionType(data.genetic_condition_type?.toString() || '');
        setGeneticConditionIcd10Code(data.genetic_condition_icd10_code || null);
        setGeneticConditionOther(data.genetic_condition_other || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von12Genetic> = {
        patient_id: patientId,
        genetic_condition_present: geneticConditionPresent,
        genetic_test_type: geneticTestType.length > 0 ? geneticTestType.join(',') : undefined,
        genetic_test_date: geneticTestDate || undefined,
        genetic_test_result: geneticTestResult ? parseInt(geneticTestResult) : undefined,
        genetic_condition_icd10_code: geneticConditionIcd10Code || undefined,
        genetic_condition_other: geneticConditionOther || undefined
      };

      if (hasExistingData) {
        await vonDataService.updateGenetic(patientId, formData);
      } else {
        await vonDataService.createGenetic(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 12: Afecțiuni genetice</CardTitle>
        <CardDescription>Date despre teste genetice și afecțiuni genetice</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Genetic testing */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Testare genetică</h3>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="geneticConditionPresent"
                checked={geneticConditionPresent}
                onCheckedChange={(checked) => setGeneticConditionPresent(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="geneticConditionPresent" className="text-sm font-normal cursor-pointer">
                Condiție genetică prezentă
              </Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="geneticTestType">Tip test genetic</Label>
                <MultiSelect
                  id="geneticTestType"
                  options={GENETIC_TEST_TYPE_OPTIONS}
                  value={geneticTestType}
                  onChange={setGeneticTestType}
                  placeholder="Selectează tipurile de teste..."
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="geneticTestDate">Data test genetic</Label>
                <Input
                  id="geneticTestDate"
                  type="date"
                  value={geneticTestDate}
                  onChange={(e) => setGeneticTestDate(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="geneticTestResult">Rezultat test genetic</Label>
                <Select value={geneticTestResult} onValueChange={setGeneticTestResult} disabled={readOnly}>
                  <SelectTrigger id="geneticTestResult">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Normal</SelectItem>
                    <SelectItem value="2">Patologic</SelectItem>
                    <SelectItem value="3">Neconcludent</SelectItem>
                    <SelectItem value="9">Necunoscut</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Genetic condition details: always shown so the ICD-10 code can be
              entered even when the "present" checkbox was not ticked */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Detalii condiție genetică</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="geneticConditionIcd10Code">Afecțiune genetică (cod ICD-10)</Label>
                <GeneticConditionAutocomplete
                  value={geneticConditionIcd10Code}
                  onChange={(code) => setGeneticConditionIcd10Code(code)}
                  isDisabled={readOnly}
                />
                {!geneticConditionIcd10Code && geneticConditionType && LEGACY_GENETIC_CONDITION_LABELS[geneticConditionType] && (
                  <p className="text-xs text-gray-500">
                    Valoare veche (necodificată): {LEGACY_GENETIC_CONDITION_LABELS[geneticConditionType]}. Selectați codul ICD-10 corespunzător și salvați pentru actualizare.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="geneticConditionOther">Altă afecțiune genetică (text)</Label>
                <Textarea
                  id="geneticConditionOther"
                  value={geneticConditionOther}
                  onChange={(e) => setGeneticConditionOther(e.target.value)}
                  placeholder="Descriere afecțiune genetică, dacă nu există cod ICD-10 corespunzător"
                  rows={3}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
